// Updated GoogleTasksSync component with useNavigate import added

import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from './Button';
import { FolderSync as Sync, AlertCircle, Clock, Settings } from 'lucide-react';

type GoogleTasksSyncProps = {
  showWhenNoTasks?: boolean;
  className?: string;
};

export function GoogleTasksSync({ showWhenNoTasks = false, className }: GoogleTasksSyncProps) {
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('manual');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [hasTasks, setHasTasks] = useState(false);
  const [hasCompletedTasks, setHasCompletedTasks] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [hasContentAccess, setHasContentAccess] = useState(false);
  const [subscription, setSubscription] = useState<{tier: string; status: string} | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('google_tasks_sync_frequency, last_google_tasks_sync')
          .eq('id', user.id)
          .single();

        if (profile) {
          setSyncFrequency(profile.google_tasks_sync_frequency || 'manual');
          setLastSyncTime(profile.last_google_tasks_sync ? new Date(profile.last_google_tasks_sync) : null);
        }

        const { data: tasks } = await supabase
          .from('tasks')
          .select('completed')
          .eq('user_id', user.id);

        if (tasks) {
          const hasAnyTasks = tasks.length > 0;
          const hasAnyCompletedTasks = tasks.some(task => task.completed);
          setHasTasks(hasAnyTasks);
          setHasCompletedTasks(hasAnyCompletedTasks);
          setShouldRender(showWhenNoTasks || hasAnyTasks);
        }

        const { data: featureAccess } = await supabase
          .rpc('check_feature_access', {
            p_user_id: user.id,
            p_feature_name: 'content_creation'
          });

        setHasContentAccess(!!featureAccess);

        const { data: subscriptionData } = await supabase
          .from('subscriptions')
          .select('tier:subscription_tiers(name), status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (subscriptionData) {
          setSubscription({
            tier: subscriptionData.tier.name,
            status: subscriptionData.status
          });
        }

      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [showWhenNoTasks]);

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      if (!response.access_token) {
        setError('No access token received from Google');
        return;
      }
      await handleSync(response.access_token);
    },
    onError: (error) => {
      console.error('Google login error:', error);
      setError('Failed to connect to Google. Please try again.');
      setIsSyncing(false);
    },
    scope: 'https://www.googleapis.com/auth/tasks https://www.googleapis.com/auth/tasks.readonly',
    flow: 'implicit',
    prompt: 'consent'
  });

  const handleSync = async (accessToken: string) => {
    setIsSyncing(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const syncResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-google-tasks`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: user.id,
            accessToken
          })
        }
      );

      if (!syncResponse.ok) {
        const errorData = await syncResponse.json();
        throw new Error(errorData.message || 'Failed to sync tasks');
      }

      const now = new Date();
      await supabase
        .from('profiles')
        .update({ last_google_tasks_sync: now.toISOString() })
        .eq('id', user.id);

      setLastSyncTime(now);
      setSuccess('Tasks synced successfully! Refresh to see changes.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error: any) {
      console.error('Error syncing tasks:', error);
      setError(error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const getFrequencyText = () => {
    switch (syncFrequency) {
      case 'hourly': return 'Syncs every hour';
      case 'daily': return 'Syncs once per day';
      default: return 'Manual sync only';
    }
  };

  if (!shouldRender) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      <Button
        variant="outline"
        onClick={() => login()}
        disabled={isSyncing}
        className="flex items-center justify-center gap-2 w-full"
      >
        <Sync className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
        {isSyncing ? 'Syncing...' : 'Sync with Google Tasks'}
      </Button>

    

  

      {success && (
        <div className="p-4 bg-green-50 text-green-700 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Success!</p>
            <p className="text-sm mt-1">{success}</p>
          </div>
        </div>
      )}
    </div>
  );
}
